import Select from "antd/es/select";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { DateInput } from "semantic-ui-calendar-react";
import { Button, Icon, Pagination } from "semantic-ui-react";
import LeaveManagementTable from "../../components/LeaveManagementTable";
import {
    clearMessageHoliday,
    createHoliday,
    deleteHoliday,
    getHolidayList,
    updateHoliday,
} from "../../core/actions/leaveHolidayAction";
import functionCommon from "../../core/common/function";
import CONSTANT from "../../core/constant/constant";
import { colorArrayHoliday, holidayType } from "../../core/constant/leaveHoliday.constants";
import "./styles.scss";

const initialParams = {
    getHolidayParam: {
        type: "",
        isSearch: 0,
        sortBy: "id-asc",
        pageRecord: 20,
    },
};

const isHR = functionCommon.getRole() === "hr";

export default function Holiday() {
    const { t } = useTranslation();
    const [data, setData] = useState(initialParams.getHolidayParam);
    const [dataError, setDataError] = useState({
        day: null,
        type: null,
    });
    const [isDisable, setIsDisable] = useState(true);
    const [activeItem, setActiveItem] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const dispatch = useDispatch();
    const leaveHoliday = useSelector((state) => state.leaveHolidayReducer);
    const { searching, adding, deleting, updating, successCode, errorCode, isLoading } = leaveHoliday;
    const conditionRef = useRef(initialParams.getHolidayParam);
    const [messageTable, setMessageTable] = useState(null);

    const handlechangeDate = (event, { name, value }) => {
        setData({ ...data, day: value });
    };

    const handleChangeType = (value) => {
        setData({ ...data, type: value });
    };

    const handleChangeActive = (index) => {
        setActiveItem(index);
        const item = leaveHoliday.listHoliday.holidays.find((ele) => ele.id === index);
        if (item) {
            setData({
                ...data,
                day: functionCommon.revertDateFormat(item.day),
                type: Number(item.type),
            });
        }
    };

    const handlePaginationChange = (e, { activePage }) => {
        setCurrentPage(activePage);
        dispatch(getHolidayList({ ...conditionRef.current, currentPage: activePage }, false, true));
    };

    const validateField = () => {
        const error = {};
        if (!data.day) {
            error.day = "labels.day";
        } else {
            error.day = null;
        }
        if (!data.type) {
            error.type = "labels.holidayType";
        } else {
            error.type = null;
        }
        setDataError(error);
        if (!error.day && !error.type) return true;
        else return false;
    };

    const handleAddHoliday = () => {
        if (validateField()) {
            setActiveItem(null);
            dispatch(
                createHoliday(
                    {
                        day: data.day,
                        type: data.type,
                    },
                    {
                        ...conditionRef.current,
                        currentPage: currentPage,
                    }
                )
            );
        }
    };

    const handleUpdateHoliday = () => {
        if (validateField()) {
            const isEndOfPage = leaveHoliday.listHoliday.holidays.length === 1;
            let newActivePage = isEndOfPage ? currentPage - 1 : currentPage;
            if (leaveHoliday.listHoliday.totalRecord === 1) {
                conditionRef.current = initialParams.getHolidayParam;
                newActivePage = 1;
            }
            setCurrentPage(newActivePage);
            setActiveItem(null);
            dispatch(
                updateHoliday(
                    {
                        id: activeItem,
                        day: data.day,
                        type: data.type,
                    },
                    {
                        ...conditionRef.current,
                        currentPage: newActivePage,
                    }
                )
            );
        }
    };

    const handleDeleteHoliday = () => {
        if (activeItem) {
            const isEndOfPage = leaveHoliday.listHoliday.holidays.length === 1;
            let newActivePage = isEndOfPage ? currentPage - 1 : currentPage;
            if (leaveHoliday.listHoliday.totalRecord === 1) {
                conditionRef.current = initialParams.getHolidayParam;
                newActivePage = 1;
            }
            setCurrentPage(newActivePage);
            setActiveItem(null);
            dispatch(
                deleteHoliday(
                    {
                        id: activeItem,
                    },
                    {
                        ...conditionRef.current,
                        currentPage: newActivePage,
                    }
                )
            );
        }
    };

    const handleSearch = () => {
        dispatch(getHolidayList({ ...data, isSearch: 1, currentPage: 1 }, true));
        conditionRef.current = { ...data, isSearch: 1 };
        setCurrentPage(1);
        setActiveItem(null);
    };

    const handleClearAll = () => {
        setData(initialParams.getHolidayParam);
        setActiveItem(null);
    };

    const triggerMessage = () => {
        setTimeout(() => {
            setMessageTable(null);
            dispatch(clearMessageHoliday());
        }, 2000);
    };

    useEffect(() => {
        setIsDisable(!activeItem);
    }, [activeItem]);

    useEffect(() => {
        dispatch(
            getHolidayList({
                ...data,
                currentPage: currentPage,
            })
        );
    }, []);

    useEffect(() => {
        if (successCode) {
            setMessageTable({ text: `message.${successCode}`, isError: false });
            triggerMessage();
            setData(initialParams.getHolidayParam);
        }
    }, [successCode]);

    useEffect(() => {
        if (errorCode) {
            setMessageTable({ text: `message.${errorCode}`, isError: true });
            triggerMessage();
        }
    }, [errorCode]);

    const holidayTable = useMemo(() => {
        const bodyTable = [];
        const colorArray = [];
        const arrayValue = [];
        const result = {
            header: [t("labels.id"), t("labels.holidayType"), t("labels.day")],
        };
        if (leaveHoliday.listHoliday.holidays) {
            leaveHoliday.listHoliday.holidays.forEach((ele, idx) => {
                bodyTable.push([
                    (Number(currentPage) - 1) * Number(leaveHoliday.listHoliday.totalPageRecord) + idx + 1,
                    holidayType[ele.type],
                    functionCommon.revertDateFormat(ele.day),
                ]);
                colorArray.push(colorArrayHoliday[ele.type]);
                arrayValue.push(ele.id);
            });
            result.body = bodyTable;
            result.colors = colorArray;
            result.arrayValue = arrayValue;
            if (!setMessageTable || setMessageTable.text !== "message.004") {
                setMessageTable(null);
            }
        } else {
            setMessageTable({ text: "message.018", isError: true });
            triggerMessage();
        }
        return result;
    }, [leaveHoliday.listHoliday, t]);

    const hidePagination = useMemo(() => {
        return searching || adding || deleting;
    });

    return (
        <div className="leave-holiday">
            <h2 className="page-heading">
                <span onClick={functionCommon.goToPage("/my-leave-request")}>{t("labels.leaveManagemnet")}</span>
                {"/ "}
                <span onClick={functionCommon.goToPage("/holiday")}>{t("labels.holiday")}</span>
            </h2>
            <div className="btn-group">
                <Button className="leave-button" onClick={handleSearch} disabled={searching}>
                    <Icon name="search" />
                    {searching ? t("button.searching") : t("button.search")}
                </Button>
                {isHR && (
                    <>
                        <Button className="leave-button" onClick={handleAddHoliday} disabled={adding}>
                            <Icon name="add circle" />
                            {adding ? t("button.adding") : t("button.add")}
                        </Button>
                        <Button className="leave-button" onClick={handleUpdateHoliday} disabled={isDisable || updating}>
                            <Icon name="edit" />
                            {updating ? t("button.updating") : t("button.update")}
                        </Button>
                        <Button onClick={handleDeleteHoliday} disabled={isDisable || deleting}>
                            <Icon name="trash alternate outline" />
                            {deleting ? t("button.deleting") : t("button.delete")}
                        </Button>
                    </>
                )}
                <Button onClick={handleClearAll}>
                    <Icon name="refresh" />
                    {t("button.clearAll")}
                </Button>
            </div>
            <div className="form-control">
                <div>
                    <div className="form select">
                        <p className="label leave-label-require ">{t("labels.holidayType")}</p>
                        <Select value={data.type} onChange={handleChangeType}>
                            <Select.Option value="">All</Select.Option>
                            {Object.values(holidayType).map((ele, idx) => (
                                <Select.Option key={idx} value={idx + 1}>
                                    {ele}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>
                    {dataError.type && (
                        <div className="error-message">{t("message.029", { field: t(dataError.type) })}</div>
                    )}
                </div>
                <div>
                    <div className="form date">
                        <p className="label leave-label-require ">{t("labels.day")}</p>
                        <DateInput
                            className="date-picker leave-date-picker leave-date-input-restrict"
                            name="date"
                            placeholder="DD-MM"
                            iconPosition="right"
                            animation="none"
                            dateFormat="DD-MM"
                            value={data.day ? data.day : ""}
                            onChange={handlechangeDate}
                            closeOnMouseLeave={false}
                            localization={localStorage.getItem("i18nextLng") === "en" ? "en" : "vi"}
                            hideMobileKeyboard={true}
                            autoComplete="off"
                            maxLength="0"
                            closable={true}
                            onKeyDown={functionCommon.onKeyDownDateInput}
                            onMouseEnter={functionCommon.onMouseEnterDateInput}
                            onMouseLeave={functionCommon.onMouseLeaveDateInput}
                        />
                    </div>
                    {messageTable && errorCode === CONSTANT.messageCode.alreadyDate && (
                        <div className="error-message">{t(messageTable.text)}</div>
                    )}
                    {dataError.day && (
                        <div className="error-message">{t("message.029", { field: t(dataError.day) })}</div>
                    )}
                </div>
            </div>
            {messageTable && errorCode !== CONSTANT.messageCode.alreadyDate && (
                <div className={`${messageTable.isError ? "error-message" : "success-message"} list-error-message`}>
                    {t(messageTable.text)}
                </div>
            )}
            <LeaveManagementTable
                content={holidayTable}
                className="custom-table"
                onActiveItem={handleChangeActive}
                activeItem={activeItem}
                isClickItem
            />
            {leaveHoliday.listHoliday.totalPage > 0 && !hidePagination && (
                <div className="leave-table-paging">
                    <Pagination
                        size="tiny"
                        activePage={currentPage}
                        firstItem={null}
                        lastItem={null}
                        disabled={isLoading}
                        totalPages={leaveHoliday.listHoliday.totalPage > 0 ? leaveHoliday.listHoliday.totalPage : null}
                        prevItem={{
                            disabled: currentPage === 1 || isLoading,
                            content: <Icon name="chevron left" />,
                            icon: true,
                        }}
                        nextItem={{
                            disabled: currentPage === leaveHoliday.listHoliday.totalPage || isLoading,
                            content: <Icon name="chevron right" />,
                            icon: true,
                        }}
                        onPageChange={handlePaginationChange}
                    />
                </div>
            )}
        </div>
    );
}
